import AddSvg from "@/src/assets/icons/icon-add-black.svg";
import ChainLinkSvg from "@/src/assets/icons/icon-chain-link.svg";
import CheckCircleBlackSvg from "@/src/assets/icons/icon-check-circle-black.svg";
import ChevronLeftSvg from "@/src/assets/icons/icon-chevron-left-black.svg";
import DocumentBlue2 from "@/src/assets/icons/icon-document-blue2.svg";
import DocumentCheck from "@/src/assets/icons/icon-document-check.svg";
import EditSvg from "@/src/assets/icons/icon-edit-white.svg";
import InfoGray6 from "@/src/assets/icons/icon-info-gray6.svg";
import Money from "@/src/assets/icons/icon-money.svg";
import PreviewSvg from "@/src/assets/icons/icon-preview-indigo.svg";
import DeleteSvg from "@/src/assets/icons/icon-delete.svg";
import Badge from "@/src/components/atom/Badge";
import { Button } from "@/src/components/atom/Button";
import Icon from "@/src/components/atom/Icon";
import Table from "@/src/components/atom/Table";
import Typo from "@/src/components/atom/Typo";
import BottomFixedContainer from "@/src/components/molecule/BottomFixedContainer";
import SectionCard from "@/src/components/molecule/SectionCard";
import SectionCardRow from "@/src/components/molecule/SectionCardRow";
import ImporterMainLayout from "@/src/components/template/Layout/importer/ImporterMainLayout";
import useAgGridHeaderRefresh from "@/src/hooks/useAgGridHeaderRefresh";
import useAlert from "@/src/hooks/useAlert";
import IMPORTER_PRIVATE_PATH from "@/src/routes/importer/path";
import {
  useCreateBuyerEmployeesMutation,
  useDeleteClientMutation,
  useGetBuyerContractExporterItemQuery,
  useGetBuyerQuery,
  useLazyGetBuyerEmployeesQuery,
} from "@/src/store/apis/client/buyer";
import {
  BuyerEmployeeDto,
  GenerateBuyerEmployeeDto,
} from "@/src/store/apis/client/buyer/interface";
import { useGetCommonCodeViaCodeNameQuery } from "@/src/store/apis/common";
import { useGetContractCountAndCumulativeAmountsByBuyerIdQuery } from "@/src/store/apis/dashboard";
import colorSet from "@/src/styles/color";
import typo from "@/src/styles/typography";
import { isNull, isUndefined } from "@/src/utils/is";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { styled } from "styled-components";
import { buyerContactPersonListColumn } from "./columns/column";
import LinkStatusBadge from "./components/LinkStatusBadge";
import BuyerEmployeeAddDialog from "./dialog/BuyerEmployeeAddDialog";
import LinkCodeInDetailPageDialog from "@/src/components/organism/LinkCodeInDetailPageDialog";
import LinkApproveDialog from "@/src/components/organism/LinkApproveDialog";
import BreakWordTypo from "@/src/components/molecule/BreakWordTypo";
import Flex from "@/src/components/molecule/Flex";
import AlertDialog from "@/src/components/atom/AlertDialog";
import { useGetSessionQuery } from "@/src/store/apis/auth";

enum DialogState {
  NULL,
  ADD,
  CLIENT_LINK_CODE,
  APPROVE_OR_REJECT_LINK_CODE,
}

enum AlertDialogState {
  NULL,
  DELETE_CLIENT,
}

const EMPLOYEE_EMPTY_ARRAY: BuyerEmployeeDto[] = [];

const ImporterClientManagementDetailPage = () => {
  const { t } = useTranslation();

  const alert = useAlert();
  const pageParams = useParams<{ id: string }>();
  const navigate = useNavigate();
  const agGridRef = useRef<AgGridReact>(null);

  const [alertDialogState, setAlertDialogState] = useState<AlertDialogState>(
    AlertDialogState.NULL
  );
  const [dialogState, setDialogState] = useState<DialogState>(DialogState.NULL);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10 });
  const [columnDefs] = useState<ColDef[]>(buyerContactPersonListColumn);

  // API
  const { exporterUserType, isManager } = useGetSessionQuery(undefined, {
    selectFromResult: ({ currentData, isError, isFetching }) => {
      const isUnstable = isError || isFetching || currentData === undefined;
      const isStable = !isUnstable;

      return {
        exporterUserType: currentData?.row.exporterUserType,
        isManager: isStable
          ? currentData.row.exporterUserType === "MANAGER"
          : false,
      };
    },
  });
  const [deleteClient, { isLoading: isDeleteClientLoading }] =
    useDeleteClientMutation();
  const {
    processingCount,
    totalCount,
    descendingCumulativeAmounts,
    isContractCountAndCumulativeAmountsFetching,
  } = useGetContractCountAndCumulativeAmountsByBuyerIdQuery(
    {
      buyerId: Number(pageParams.id),
    },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ isFetching, currentData, isError }) => {
        const isUnstable = isUndefined(currentData) || isError || isFetching;

        return {
          isContractCountAndCumulativeAmountsFetching: isFetching,
          processingCount: !isUnstable
            ? Number(currentData.processingCount)
            : 0,
          totalCount: !isUnstable ? Number(currentData.totalCount) : 0,
          descendingCumulativeAmounts: !isUnstable
            ? currentData.descendingCumulativeAmounts
            : [],
        };
      },
    }
  );

  const {
    isFetching,
    isError,
    clientId,
    buyerName,
    buyerNameCode,
    country,
    businessRegistrationSimpleMedia,
    businessNumber,
    mainCategoryCodeItemNames,
    address,
    tel,
    fax,
    isActivated,
    countryValue,
    linkedStatus,
    defaultClientCode,
    isReceivedLinkRequest,
    isUnstableBuyer,
  } = useGetBuyerQuery(
    {
      id: Number(pageParams.id),
    },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ isFetching, currentData, isError }) => {
        const isUnstable = isUndefined(currentData) || isError || isFetching;

        return {
          isUnstableBuyer: isUnstable,
          isFetching,
          isError,
          clientId: !isUnstable ? currentData.id : 0,
          buyerName: !isUnstable ? currentData.buyerName : "-",
          buyerNameCode: !isUnstable ? currentData.buyerNameCode : "-",
          country: !isUnstable ? currentData.country : "-",
          businessRegistrationSimpleMedia:
            !isUnstable && !isNull(currentData.businessRegistrationSimpleMedia)
              ? currentData.businessRegistrationSimpleMedia
              : null,
          businessNumber:
            !isUnstable && !isNull(currentData.businessNumber)
              ? currentData.businessNumber
              : "-",
          address: !isUnstable ? currentData.address : "-",
          mainCategoryCodeItemNames: !isUnstable
            ? currentData.mainCategoryCodeItemNames
            : [],
          tel:
            !isUnstable &&
            !isNull(currentData.fullTel) &&
            !isNull(currentData.buyerQueryResultDto.tel) &&
            !isNull(currentData.buyerQueryResultDto.telPrefix)
              ? currentData.fullTel
              : "-",
          fax:
            !isUnstable &&
            !isNull(currentData.fullFax) &&
            !isNull(currentData.buyerQueryResultDto.fax) &&
            !isNull(currentData.buyerQueryResultDto.faxPrefix)
              ? currentData.fullFax
              : "-",
          isActivated: !isUnstable ? currentData.isActivated : false,
          countryValue: !isUnstable
            ? currentData.buyerQueryResultDto.country.value1
            : undefined,
          linkedStatus: !isUnstable
            ? currentData.buyerQueryResultDto.linkedStatus
            : "PENDING",
          defaultClientCode: !isUnstable
            ? (currentData.buyerQueryResultDto.companyLinkageHistory?.sender
                ?.companyName ?? "")
            : "",
          isReceivedLinkRequest: !isUnstable
            ? currentData.buyerQueryResultDto.linkedStatus === "RECEIVED"
            : false,
        };
      },
    }
  );

  const { currentData } = useGetBuyerContractExporterItemQuery(
    {
      id: Number(pageParams.id),
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const [getEmployees, { isEmployeeFetching, employeeList, employeeCount }] =
    useLazyGetBuyerEmployeesQuery({
      selectFromResult: ({ currentData, isError, isFetching }) => {
        const isUnstable = isError || isUndefined(currentData);
        return {
          isEmployeeFetching: isFetching,
          employeeList: !isUnstable ? currentData.rows : EMPLOYEE_EMPTY_ARRAY,
          employeeCount: !isUnstable ? currentData.count : 0,
        };
      },
    });

  const { isMainCategoryFetching, mainCategory } =
    useGetCommonCodeViaCodeNameQuery(
      {
        codeName: "MAIN_CATEGORY",
      },
      {
        refetchOnMountOrArgChange: true,
        selectFromResult: ({ currentData, isError, isFetching }) => {
          const isErrorAndUndefined = isError || currentData === undefined;

          return {
            isMainCategoryFetching: isFetching,
            mainCategory: !isErrorAndUndefined ? (currentData ?? []) : [],
          };
        },
      }
    );
  const [createEmployee] = useCreateBuyerEmployeesMutation();

  useAgGridHeaderRefresh({
    gridRef: agGridRef.current,
    isReady,
    headerSet: [
      {
        columnKey: "name",
        langKey: "clientManagement:importer.common.name",
      },
      {
        columnKey: "email",
        langKey: "clientManagement:importer.common.email",
      },
      {
        columnKey: "personalContact",
        langKey: "clientManagement:importer.common.personalContact",
      },
      {
        columnKey: "officeContact",
        langKey: "clientManagement:importer.common.officeContact",
      },
      {
        columnKey: "buyerEmployeeRemark",
        langKey: "clientManagement:importer.common.remark",
      },
      {
        columnKey: "isActivated",
        langKey: "clientManagement:importer.common.use",
      },
    ],
  });

  const fetchEmployees = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    try {
      await getEmployees({
        id: Number(pageParams.id),
        page,
        pageSize,
      }).unwrap();
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  const isDeleteButtonRender =
    linkedStatus === "PENDING" &&
    processingCount + totalCount === 0 &&
    !isContractCountAndCumulativeAmountsFetching &&
    !isUnstableBuyer;

  const basicInformationCardLeftSideItemList = [
    {
      label: t("clientManagement:importer.common.clientName"),
      value: buyerName,
    },
    {
      label: t("clientManagement:importer.common.clientCode"),
      value: buyerNameCode,
    },
    {
      label: t("clientManagement:importer.common.country"),
      value: country,
    },
    {
      label: t("clientManagement:importer.buyerDetail.categoryInCharge"),
      value: !isMainCategoryFetching
        ? mainCategoryCodeItemNames
            .map(
              (main) =>
                mainCategory.find(({ codeItemName }) => codeItemName === main)
                  ?.codeItemNameEn
            )
            .join(", ")
        : "-",
    },
    {
      label: t("clientManagement:importer.common.businessNumber"),
      value: businessNumber,
    },
    {
      label: t("clientManagement:importer.common.businessRegistration"),
      value: businessRegistrationSimpleMedia?.originalFileName ? (
        <RowValueContainer>
          {businessRegistrationSimpleMedia.originalFileName}{" "}
          <StyledIconButton
            buttonGrade="secondary"
            buttonSize={24}
            buttonColor="blue"
            onClick={() => {
              if (businessRegistrationSimpleMedia) {
                window.open(businessRegistrationSimpleMedia.mediaUrl);
              }
            }}
          >
            <Icon iconSrc={PreviewSvg} iconSize={16} />
          </StyledIconButton>
        </RowValueContainer>
      ) : (
        "-"
      ),
    },
  ];

  const basicInformationCardRightSideItemList = [
    {
      label: t("signupCorp:content.address"),
      value: address,
    },
    {
      label: t("clientManagement:importer.common.contact"),
      value: tel,
    },
    {
      label: t("clientManagement:importer.common.fax"),
      value: fax,
    },
    {
      label: t("clientManagement:importer.common.use"),
      value: isActivated ? (
        <Badge
          text={t("status:yes")}
          badgeSize="S"
          badgeColor="systemLime5"
          color="systemLime1"
        />
      ) : (
        <Badge
          text={t("status:no")}
          badgeSize="S"
          badgeColor="gray10"
          color="gray7"
        />
      ),
    },
  ];

  const handleSubmit = async (
    values: Omit<GenerateBuyerEmployeeDto, "isActivated"> & {
      isActivated: string;
    }
  ) => {
    try {
      setDialogState(DialogState.NULL);
      await createEmployee({
        id: Number(pageParams.id),
        name: values.name,
        email: values.email,
        isActivated: values.isActivated === "Y",
        personalContactPrefix: values.personalContactPrefix || undefined,
        personalContact: values.personalContact || undefined,
        officeContactPrefix: values.officeContactPrefix || undefined,
        officeContact: values.officeContact || undefined,
        buyerEmployeeRemark: values.buyerEmployeeRemark || undefined,
      }).unwrap();
      await fetchEmployees({
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    } finally {
    }
  };

  const handleDeleteClientClick = async () => {
    setAlertDialogState(AlertDialogState.DELETE_CLIENT);
  };

  const handleDeleteConfirmClient = async () => {
    try {
      await deleteClient({ id: Number(pageParams.id) }).unwrap();
      navigate(IMPORTER_PRIVATE_PATH.CLIENT_MANAGEMENT);

      alert.showAlert({
        type: "success",
        message: t("alert:clientDeleteSuccess"),
      });
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  const renderDialog = () => {
    const closeDialog = () => setDialogState(DialogState.NULL);

    if (dialogState === DialogState.ADD) {
      return (
        <BuyerEmployeeAddDialog
          defaultCountryCode={countryValue}
          onSave={handleSubmit}
          onClose={closeDialog}
        />
      );
    }

    if (dialogState === DialogState.CLIENT_LINK_CODE) {
      return (
        <LinkCodeInDetailPageDialog
          onClose={closeDialog}
          skipSelectClient
          clientId={clientId}
        />
      );
    }

    if (dialogState === DialogState.APPROVE_OR_REJECT_LINK_CODE) {
      return (
        <LinkApproveDialog
          onClose={closeDialog}
          clientId={Number(pageParams.id)}
          defaultClientCode={defaultClientCode}
          type="BUYER"
        />
      );
    }
  };

  const renderAlertDialog = () => {
    const closeDialog = () => setAlertDialogState(AlertDialogState.NULL);

    if (alertDialogState === AlertDialogState.DELETE_CLIENT) {
      return (
        <AlertDialog
          open
          title={t("common:clientDelete")}
          onOpenChange={closeDialog}
          onOk={handleDeleteConfirmClient}
          okText={t("common:delete")}
          cancelText={t("common:cancel")}
          isOkLoading={isDeleteClientLoading}
          type="delete"
        >
          {t("common:clientDeleteDescription")}
        </AlertDialog>
      );
    }
  };

  const renderFooter = () => {
    return (
      <BottomFixedContainer>
        <Flex justifyContent="space-between">
          <BackButton
            buttonGrade="tertiary"
            buttonColor="black"
            onClick={() => navigate(-1)}
            style={{ display: "flex", alignItems: "center", gap: "4px" }}
          >
            <Icon iconSrc={ChevronLeftSvg} iconSize={16} />{" "}
            {t("clientManagement:importer.common.backToPrevious")}
          </BackButton>
          {isDeleteButtonRender && (
            <BackButton
              buttonGrade="secondary"
              buttonColor="red"
              onClick={handleDeleteClientClick}
              style={{ display: "flex", alignItems: "center", gap: "4px" }}
            >
              <Icon iconSrc={DeleteSvg} iconSize={16} />{" "}
              {t("common:clientDelete")}
            </BackButton>
          )}
        </Flex>
      </BottomFixedContainer>
    );
  };

  useEffect(() => {
    fetchEmployees({ page: 1, pageSize: 10 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (agGridRef.current && isEmployeeFetching) {
      agGridRef.current.api.showLoadingOverlay();
    }
  }, [isEmployeeFetching]);

  useEffect(() => {
    if (exporterUserType === "CORPORATE_MANAGER" && isReceivedLinkRequest) {
      setDialogState(DialogState.APPROVE_OR_REJECT_LINK_CODE);
    }
  }, [isReceivedLinkRequest, exporterUserType]);

  return (
    <ImporterMainLayout
      breadcrumb={[
        t("sideNav:clients"),
        t("sideNav:clientManagementDetail"),
      ]}
      customPageTitle={
        <PageTitleContainer>
          <h1>{buyerName}</h1>

          <FlexSpaceBetween className="pb-16">
            <LinkStatusBadge status={linkedStatus} badgeSize="L" t={t} />

            <FlexDiv>
              {linkedStatus === "PENDING" &&
                !isFetching &&
                !isError &&
                exporterUserType === "CORPORATE_MANAGER" && (
                  <StyledButton
                    buttonColor="blue"
                    buttonGrade="secondary"
                    buttonSize={32}
                    onClick={() => setDialogState(DialogState.CLIENT_LINK_CODE)}
                  >
                    <Icon iconSrc={ChainLinkSvg} iconSize={16} />
                    {t(
                      "clientManagement:importer.buyerDetail.buttons.companyIntegrationCode"
                    )}
                  </StyledButton>
                )}
              {linkedStatus === "RECEIVED" &&
                exporterUserType === "CORPORATE_MANAGER" && (
                  <StyledButton
                    buttonColor="blue"
                    buttonGrade="secondary"
                    buttonSize={32}
                    onClick={() =>
                      setDialogState(DialogState.APPROVE_OR_REJECT_LINK_CODE)
                    }
                  >
                    <Icon iconSrc={CheckCircleBlackSvg} iconSize={16} />
                    {t(
                      "clientManagement:importer.buyerDetail.buttons.requestCompleted"
                    )}
                  </StyledButton>
                )}
              <StyledButton
                buttonSize={32}
                onClick={() =>
                  navigate(
                    `${IMPORTER_PRIVATE_PATH.CLIENT_MANAGEMENT_EDIT}/${pageParams.id}`
                  )
                }
              >
                <Icon iconSrc={EditSvg} iconSize={16} />
                {t("clientManagement:importer.common.edit")}
              </StyledButton>
            </FlexDiv>
          </FlexSpaceBetween>
        </PageTitleContainer>
      }
    >
      <SectionContainer>
        <SummaryContainer>
          <SummaryCard $gap={16}>
            <SummaryDiv>
              <Icon iconSrc={DocumentBlue2} iconSize={56} />
              <div>
                <Typo typoType="b6m">
                  {t("clientManagement:importer.summary.processingContract")}
                </Typo>
                <Typo typoType="d4" color="blue4">
                  {processingCount.toLocaleString("ko-KR")}
                  {t("common:cases.ko")}
                </Typo>
              </div>
            </SummaryDiv>
            <SummaryContractDivider />
            <SummaryDiv>
              <Icon iconSrc={DocumentCheck} iconSize={56} />
              <div>
                <Typo typoType="b6m">
                  {t("clientManagement:importer.summary.totalContract")}
                </Typo>
                <Typo typoType="d4" color="blue4">
                  {totalCount.toLocaleString("ko-KR")}
                  {t("common:cases.ko")}
                </Typo>
              </div>
            </SummaryDiv>
          </SummaryCard>
          <SummaryCard $gap={32}>
            <SummaryAmountDiv>
              <SummaryDiv $withoutPadding>
                <Icon iconSrc={Money} iconSize={56} />
                <Typo typoType="b6m">
                  {t("clientManagement:importer.summary.cumulativeAmount")}
                </Typo>
              </SummaryDiv>
              <SummaryAmountNotice>
                <Icon iconSrc={InfoGray6} iconSize={16} />
                <Typo typoType="b9r" color="gray6">
                  {t("clientManagement:importer.summary.summaryAmountNotice")}
                </Typo>
              </SummaryAmountNotice>
            </SummaryAmountDiv>
            {descendingCumulativeAmounts.length > 0 ? (
              <SummaryAmountList>
                {descendingCumulativeAmounts
                  .slice(0, 3)
                  .map((amount, index) => (
                    <SummaryAmountListItem
                      key={"amount-" + amount.unitPriceUnit}
                    >
                      <Typo typoType="b6m" color="gray2">
                        {amount.unitPriceUnit}
                      </Typo>
                      <Typo typoType="h4" color="blue4">
                        {amount.amount}
                      </Typo>
                    </SummaryAmountListItem>
                  ))}
              </SummaryAmountList>
            ) : (
              <SummaryAmountList>
                <SummaryAmountListItem>
                  <Typo typoType="b6m" color="gray2">
                    KRW
                  </Typo>
                  <Typo typoType="h4" color="blue4">
                    -
                  </Typo>
                </SummaryAmountListItem>
                <SummaryAmountListItem>
                  <Typo typoType="b6m" color="gray2">
                    USD
                  </Typo>
                  <Typo typoType="h4" color="blue4">
                    -
                  </Typo>
                </SummaryAmountListItem>
                <SummaryAmountListItem>
                  <Typo typoType="b6m" color="gray2">
                    EU
                  </Typo>
                  <Typo typoType="h4" color="blue4">
                    -
                  </Typo>
                </SummaryAmountListItem>
              </SummaryAmountList>
            )}
          </SummaryCard>
        </SummaryContainer>

        <SectionCard
          cardTitle={t("clientManagement:importer.common.basicInformation")}
        >
          <StyledDivContainer>
            <HalfDiv>
              {basicInformationCardLeftSideItemList.map(({ label, value }) => {
                return (
                  <SectionCardRow
                    label={label}
                    direction="horizontal"
                    value={
                      <BreakWordTypo typoType="b7r" color="gray5">
                        {value}
                      </BreakWordTypo>
                    }
                  />
                );
              })}
            </HalfDiv>
            <Divider />
            <HalfDiv>
              {basicInformationCardRightSideItemList.map(({ label, value }) => {
                return (
                  <SectionCardRow
                    label={label}
                    direction="horizontal"
                    value={
                      <BreakWordTypo typoType="b7r" color="gray5">
                        {value}
                      </BreakWordTypo>
                    }
                  />
                );
              })}
            </HalfDiv>
          </StyledDivContainer>
        </SectionCard>

        <SectionCard
          cardTitle={t("clientManagement:importer.buyerDetail.tradedItems")}
        >
          <BreakWordTypo typoType="b7r" color="gray5">
            {currentData
              ? currentData.rows.map(({ item }) => item).join(", ")
              : "-"}
          </BreakWordTypo>
        </SectionCard>

        <SectionCard
          cardTitle={t("clientManagement:importer.common.contactPersonList")}
          rightAccessory={
            <StyledButton
              buttonGrade="tertiary"
              buttonSize={32}
              buttonColor="black"
              onClick={() => setDialogState(DialogState.ADD)}
            >
              <Icon iconSrc={AddSvg} iconSize={16} />
              <Typo typoType="btn3m" color="gray2">
                {t(
                  "clientManagement:importer.buyerDetail.buttons.addContactPerson"
                )}
              </Typo>
            </StyledButton>
          }
        >
          <Table
            ref={agGridRef}
            rowData={employeeList}
            columnDefs={columnDefs}
            isPaginationDataMaping
            totalPage={employeeCount}
            page={pagination.page}
            pageSize={pagination.pageSize}
            onGridReady={() => setIsReady(true)}
            handlePaginationClick={(page, pageSize) => {
              fetchEmployees({ page, pageSize });
              setPagination({ page, pageSize });
            }}
            context={{ isManager }}
          />
        </SectionCard>

        {renderFooter()}
        {renderDialog()}
        {renderAlertDialog()}
      </SectionContainer>
    </ImporterMainLayout>
  );
};

export default ImporterClientManagementDetailPage;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  padding-bottom: 72px;
`;

const StyledDivContainer = styled.div`
  display: flex;
  gap: 24px;
`;

const HalfDiv = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Divider = styled.div`
  padding: 0.5px;
  background-color: ${colorSet.gray9};
`;

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledIconButton = styled(Button)`
  display: flex;
  align-items: center;
  padding: 4px;
`;

const RowValueContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PageTitleContainer = styled.div`
  display: flex;
  flex-direction: column;

  h1 {
    ${typo.h1}
    padding: 15px 0;
  }
`;

const FlexDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const FlexSpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  &.pb-16 {
    padding-bottom: 16px;
  }
`;

const BackButton = styled(Button)`
  ${typo.btn3m}
  display: inline-flex;
  align-self: flex-start;
`;

const SummaryContainer = styled.div`
  display: flex;
  gap: 16px;
`;

const SummaryCard = styled.div<{ $gap?: 16 | 32 }>`
  padding: 24px;
  border-radius: 16px;
  box-shadow: 0px 4px 15px 0px rgba(0, 0, 0, 0.05);
  flex: 1 1 0%;
  display: flex;
  align-items: center;
  gap: ${(props) => (props.$gap === 32 ? "32px" : "16px")};
`;

const SummaryDiv = styled.div<{ $withoutPadding?: boolean }>`
  flex: 1 1 0%;
  padding: ${(props) => (props.$withoutPadding ? "0" : "16px")};
  display: flex;
  align-items: center;
  gap: 12px;

  & > div {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
`;

const SummaryContractDivider = styled.div`
  border-right-color: ${colorSet.gray9};
  border-right-width: 1px;
  border-right-style: solid;
  height: 100%;
`;

const SummaryAmountDiv = styled.div`
  display: flex;
  flex: 1 1 0%;
  flex-direction: column;
  gap: 12px;
`;

const SummaryAmountNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SummaryAmountList = styled.ul`
  flex: 1 1 0%;
`;

const SummaryAmountListItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;

  &:not(:first) {
    margin-top: 4px;
  }

  *:first-child {
    width: 40px;
  }
`;
